import { notify } from '@billy/design-system'
import noop from 'lodash/noop'
import {
  createContext,
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import { InvoiceDiscountMode } from '@features/invoices/enums/invoiceDiscountMode'
import { DepositInvoiceConfig } from '@features/invoices/types/invoiceDeposit/depositInvoiceConfig'
import { InvoiceFormReceiver } from '@features/invoices/types/invoiceForm/invoiceFormReceiver'
import { getInvoiceNumberDisplayedValue } from '@features/invoices/utils/getInvoiceNumberDisplayedValue'
import { getTrackingDocumentType } from '@features/invoices/utils/getTrackingDocumentType'
import { hasInvoiceLineDiscount } from '@features/invoices/utils/hasInvoiceLineDiscount'

import { useDirtyRoute } from '../../../../../contexts/dirtyRouteContext'
import { NotificationKeys } from '../../../../../enums/notificationKeys'
import { UseFormReturn, useSegment } from '../../../../../hooks'
import { InvoiceEditorVariant } from '../enums/invoiceEditorVariant'
import { useInvoiceEditorBillingStage } from '../hooks/useInvoiceEditorBillingStage'
import { useInvoiceEditorDocumentType } from '../hooks/useInvoiceEditorDocumentType'
import { useInvoiceIssueDateLimits } from '../hooks/useInvoiceIssueDateLimits'
import { useResetFormFieldOnChange } from '../hooks/useResetFormFieldOnChange'
import { InvoiceFormSchema } from '../types/invoiceFormSchema'
import { PredefinedInvoiceValues } from '../types/predefinedInvoiceValues'
import { getIsEditorVariantRecurringInvoicesSwitch } from '../utils/getIsEditorVariantRecurringInvoicesSwitch'
import { getInvoiceFormSchema } from '../utils/invoiceEditorForm/getInvoiceFormSchema'
import { useInvoiceEditor } from './invoiceEditorContext'
import { useInvoiceEditorDepositModal } from './invoiceEditorDepositModalContext'
import { useInvoiceEditorForms } from './invoiceEditorFormsContext'
import { useInvoiceEditorValues } from './invoiceEditorValuesContext'

interface ContextState {
  discountMode: InvoiceDiscountMode
  displayedInvoiceNumber: string
  invoiceEditorForm: UseFormReturn<InvoiceFormSchema>
  isLoading: boolean
  predefinedInvoiceValues: PredefinedInvoiceValues
  setDiscountMode: Dispatch<SetStateAction<InvoiceDiscountMode>>
  trackInvoiceEditor: (eventDetail: string, payload?: Record<string, any>) => void
  validate: () => Promise<boolean>
}

const InvoiceEditorFormContext = createContext<ContextState | undefined>(undefined)

interface InvoiceEditorFormContextProviderProps {
  children?: ReactNode
}

export const InvoiceEditorFormContextProvider = ({ children }: InvoiceEditorFormContextProviderProps): ReactElement => {
  const { t } = useTranslation()
  const [discountMode, setDiscountMode] = useState(InvoiceDiscountMode.None)

  // the variant the previous render ran with, so a toggle is spotted on the render it happens
  const renderedEditorVariant = useRef<InvoiceEditorVariant | undefined>()
  const isSwitchingRecurringInvoicesVariant = useRef(false)
  // the variant the form currently holds values for
  const seededEditorVariant = useRef<InvoiceEditorVariant | undefined>()

  // const previousEditorVariant = useRef<InvoiceEditorVariant | undefined>()

  const { track } = useSegment()
  const { setDirty } = useDirtyRoute()
  const { invoiceValues, isLoading, predefinedInvoiceValues } = useInvoiceEditorValues()
  const { documentType, isCreditNote, isInvoice, isQuote, isRecurringInvoice } = useInvoiceEditorDocumentType()
  const { invoiceEditorVariant } = useInvoiceEditor()
  const billingStage = useInvoiceEditorBillingStage()
  const trackingDocumentType = useMemo(
    () => getTrackingDocumentType({ billingStage, documentType }),
    [billingStage, documentType],
  )

  const { invoiceEditorForm, setInvoiceEditorFormSchema } = useInvoiceEditorForms()
  const { depositConfig } = useInvoiceEditorDepositModal()

  const {
    Form: InvoiceEditorFormProvider,
    formState: { isDirty, errors },
    handleSubmit,
    trigger,
    reset: resetInvoiceForm,
    setValue,
    watch,
  } = invoiceEditorForm

  // Clicking the Recurring Invoice toggle switches the editor variant while the user is
  // working, and the values hooks hand over a fresh set of values for the other document type
  // straight away. The form has to keep what the user already has, so that one change seeds
  // nothing - neither through the reset below nor through the field syncs.
  //
  // Those values arrive as soon as the variant changes, whether or not anything is still
  // loading, so spotting the toggle cannot wait for loading the way seeding does. The flag
  // stays up until the variant changes again, which also covers the values settling
  // afterwards.
  if (renderedEditorVariant.current !== invoiceEditorVariant) {
    const previousVariant = renderedEditorVariant.current
    renderedEditorVariant.current = invoiceEditorVariant
    isSwitchingRecurringInvoicesVariant.current =
      !!previousVariant && getIsEditorVariantRecurringInvoicesSwitch(previousVariant, invoiceEditorVariant)
  }

  useResetFormFieldOnChange<DepositInvoiceConfig>({
    enabled: !isSwitchingRecurringInvoicesVariant.current,
    fieldKey: 'invoiceLines',
    currentValue: depositConfig,
    setValue,
    valueToSet: invoiceValues.invoiceLines,
  })

  useResetFormFieldOnChange<InvoiceFormReceiver>({
    enabled: !isSwitchingRecurringInvoicesVariant.current,
    fieldKey: 'receiver',
    currentValue: invoiceValues.receiver,
    setValue,
    valueToSet: invoiceValues.receiver,
  })

  const [invoiceNumber, invoiceNumberPrefix] = watch(['invoiceNumber', 'invoiceNumberPrefix'])

  const { fiscalLockingDate, latestInvoiceIssueDate } = useInvoiceIssueDateLimits({
    invoiceNumberPrefix,
    isInvoice,
  })

  const displayedInvoiceNumber = useMemo(
    () => getInvoiceNumberDisplayedValue(invoiceNumber, invoiceNumberPrefix),
    [invoiceNumber, invoiceNumberPrefix],
  )

  const trackInvoiceEditor = useCallback(
    (eventDetail: string, payload?: Record<string, any>) => {
      const teamPrefix = eventDetail === 'viewed' ? 'XXX - AG' : 'XXX - Invoicing Core'
      const eventName = `${teamPrefix} - Invoice Editor - ${eventDetail}`

      track(eventName, {
        ...payload,
        documentType: trackingDocumentType,
        invoiceNumber: displayedInvoiceNumber,
      })
    },
    [displayedInvoiceNumber, trackingDocumentType, track],
  )

  // we're using a fake submit for validation due to how useForm's mode param works
  const handleValidation = useCallback(async () => {
    await handleSubmit(noop)()
    return trigger()
  }, [handleSubmit, trigger])

  useEffect(() => {
    if (isInvoice || isQuote || isRecurringInvoice) {
      // we need the document number prefix value to determine values required
      // for validation, hence we cannot define the final schema upfront
      const validationSchema = getInvoiceFormSchema({
        fiscalLockingDate,
        isCreditNote,
        isQuote,
        isRecurringInvoice,
        latestInvoiceIssueDate,
        t,
      })
      setInvoiceEditorFormSchema(validationSchema)
    }
  }, [
    fiscalLockingDate,
    isCreditNote,
    isInvoice,
    isQuote,
    isRecurringInvoice,
    latestInvoiceIssueDate,
    setInvoiceEditorFormSchema,
    t,
  ])

  // Seeding waits for the values to finish loading, so the form is filled from the document
  // rather than from the defaults standing in for it while it is on its way.
  useEffect(() => {
    const isFormSeededForVariant = seededEditorVariant.current === invoiceEditorVariant

    if (isLoading || isSwitchingRecurringInvoicesVariant.current || isFormSeededForVariant) {
      return
    }

    seededEditorVariant.current = invoiceEditorVariant
    resetInvoiceForm(invoiceValues)

    // Derived alongside the reset rather than in an effect of its own, so discountMode can only
    // ever be re-derived when the form is actually being seeded with these values - an
    // in-progress selection isn't wiped out.
    const hasLineDiscount = hasInvoiceLineDiscount(invoiceValues.invoiceLines)
    if (hasLineDiscount) {
      setDiscountMode(InvoiceDiscountMode.ByItem)
    } else if (invoiceValues.discount > 0) {
      setDiscountMode(InvoiceDiscountMode.Global)
    } else {
      setDiscountMode(InvoiceDiscountMode.None)
    }
  }, [invoiceEditorVariant, invoiceValues, isLoading, resetInvoiceForm])

  useEffect(() => {
    setDirty(isDirty)
    // Run effect only on `isDirty` value change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty])

  // Show error notification if there are validation errors in invoice lines (credit note document)
  useEffect(() => {
    const errorMessage = errors.root?.message
    if (errorMessage) {
      notify({ id: NotificationKeys.InvoiceEditorFormValidationError, message: errorMessage, variant: 'error' })
    }
  }, [errors.root])

  const contextValue = useMemo(
    () => ({
      discountMode,
      displayedInvoiceNumber,
      invoiceEditorForm,
      isLoading,
      predefinedInvoiceValues,
      setDiscountMode,
      trackInvoiceEditor,
      validate: handleValidation,
    }),
    [
      discountMode,
      displayedInvoiceNumber,
      handleValidation,
      invoiceEditorForm,
      isLoading,
      predefinedInvoiceValues,
      setDiscountMode,
      trackInvoiceEditor,
    ],
  )

  return (
    <InvoiceEditorFormContext.Provider value={contextValue}>
      <InvoiceEditorFormProvider>{children}</InvoiceEditorFormProvider>
    </InvoiceEditorFormContext.Provider>
  )
}

export const useInvoiceEditorForm = () => {
  const context = useContext(InvoiceEditorFormContext)

  if (!context) {
    throw new Error('InvoiceEditorFormContextProvider is missing in the module!')
  }

  return context
}
